import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Ban, CheckCircle2, Edit, MoreHorizontal, Plus, Search, ShieldCheck, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { catalogRolesApi, type CatalogRole, type CatalogRoleGroup } from '@/services/catalog-roles'
import { workersApi } from '@/services/workers'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import { unmaskMaskedId } from '@/lib/backend-ids'
import { getServiceInstanceIdFromSearch, getServiceInstanceIdFromToken } from '@/lib/service-instance'
import { withServiceInstanceId } from '@/lib/service-instance'
import { useAuthStore } from '@/stores/auth-store'

type FormState = {
  id: string | null
  role: string
  roleGroup: string
  maxAssignmentCount: string
  primaryRole: boolean
  appliesToOrganization: boolean
  isSystemManaged: boolean
}

type GroupFormState = {
  id: string | null
  title: string
}

const initialFormState: FormState = {
  id: null,
  role: '',
  roleGroup: '',
  maxAssignmentCount: '',
  primaryRole: false,
  appliesToOrganization: false,
  isSystemManaged: false,
}

const initialGroupFormState: GroupFormState = {
  id: null,
  title: '',
}

const SHOW_CATALOG_ROLE_GROUPS_UI = false

const DEFAULT_STATUS_FILTER: 'active' = 'active'

const readStatusFilterFromParams = (value: string | null): 'all' | 'active' | 'inactive' => {
  if (value === 'all' || value === 'inactive') {
    return value
  }
  return DEFAULT_STATUS_FILTER
}

const isCatalogRoleActive = (role: CatalogRole) => {
  const active = role.active
  if (active === false || active === 0) {
    return false
  }
  if (active === true || active === 1) {
    return true
  }
  const normalized = String(active ?? '1').trim().toLowerCase()
  return normalized !== '0' && normalized !== 'false'
}

const readRoleGroupId = (role: CatalogRole): string => String(role.role_group_id ?? role.role_group ?? '').trim()
const readRoleGroupValue = (group: CatalogRoleGroup): string => String(group.encrypted_id ?? group.id ?? '').trim()
const readRoleCode = (role: CatalogRole): string => String(role.code || role.unique_id || role.role_level_code || '').trim()
const readBackendWorkerSkillCount = (role: CatalogRole): number => {
  const count = Number(role.worker_skill_count ?? Number.NaN)
  return Number.isFinite(count) ? count : 0
}

const getScopeLabel = (scopeType?: string | null) => {
  if (scopeType === 'organization') {
    return 'Global'
  }

  if (scopeType === 'service_instance') {
    return 'Local'
  }

  return 'System'
}

export default function CatalogRolesPage() {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const searchParamsText = searchParams.toString()
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '')
  const [selectedRoleGroupFilter, setSelectedRoleGroupFilter] = useState(() => searchParams.get('group') ?? '__all__')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>(() => readStatusFilterFromParams(searchParams.get('status')))
  const [groupSearch, setGroupSearch] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [formState, setFormState] = useState<FormState>(initialFormState)
  const [deactivateTarget, setDeactivateTarget] = useState<CatalogRole | null>(null)
  const [groupFormOpen, setGroupFormOpen] = useState(false)
  const [groupFormState, setGroupFormState] = useState<GroupFormState>(initialGroupFormState)
  const [deleteGroupTarget, setDeleteGroupTarget] = useState<CatalogRoleGroup | null>(null)
  const accessToken = useAuthStore((state) => state.accessToken)
  const storedServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const currentPost = useAuthStore((state) => state.currentPost)

  const currentPostServiceInstanceId = currentPost?.service_instance
    ? String(currentPost.service_instance)
    : currentPost?.service_instance_id
      ? String(currentPost.service_instance_id)
      : null
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
    || currentPostServiceInstanceId
    || storedServiceInstanceId
    || getServiceInstanceIdFromToken(accessToken)

  const rolesQuery = useQuery({
    queryKey: ['catalog-roles', serviceInstanceId],
    queryFn: () => catalogRolesApi.list({
      serviceInstanceId: serviceInstanceId || undefined,
      includeHidden: true,
    }),
    staleTime: 30_000,
  })

  const groupsQuery = useQuery({
    queryKey: ['catalog-role-groups', serviceInstanceId],
    queryFn: () => catalogRolesApi.listGroups({
      serviceInstanceId: serviceInstanceId || undefined,
    }),
    staleTime: 30_000,
  })

  const formGroupsQuery = useQuery({
    queryKey: ['catalog-role-form-groups', serviceInstanceId],
    queryFn: () => catalogRolesApi.listGroups({
      serviceInstanceId: serviceInstanceId || undefined,
    }),
    enabled: formOpen,
    staleTime: 30_000,
  })

  const workerSkillsQuery = useQuery({
    queryKey: ['worker-skills', 'catalog-role-counts', serviceInstanceId],
    queryFn: () => workersApi.getWorkerSkills({
      service_instance_id: serviceInstanceId || undefined,
      is_active: true,
      page_size: 500,
    }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 30_000,
  })

  const groups = groupsQuery.data ?? []

  const workerSkillCountsByRole = useMemo(() => {
    const counts = new Map<string, number>()
    for (const skill of workerSkillsQuery.data ?? []) {
      if (skill.is_active === false) {
        continue
      }

      const roleKeys = [
        skill.skill_role,
        skill.skill_role_unique_id,
      ]
        .map((value) => String(value ?? '').trim())
        .filter(Boolean)

      for (const key of roleKeys) {
        counts.set(key, (counts.get(key) ?? 0) + 1)
      }
    }
    return counts
  }, [workerSkillsQuery.data])

  const readWorkerSkillCount = (role: CatalogRole): number => {
    const backendCount = readBackendWorkerSkillCount(role)
    if (backendCount > 0) {
      return backendCount
    }

    const rawRoleId = unmaskMaskedId(role._id)
    const roleKeys = [
      role.id,
      role._id,
      rawRoleId,
      role.unique_id,
    ]
      .map((value) => String(value ?? '').trim())
      .filter(Boolean)

    return roleKeys.reduce((maxCount, key) => Math.max(maxCount, workerSkillCountsByRole.get(key) ?? 0), 0)
  }

  const buildSkillWorkersPath = (role: CatalogRole): string => {
    const rawRoleId = unmaskMaskedId(role._id)
    return withServiceInstanceId(`/jobs/workers/skill/${rawRoleId ?? role._id ?? role.id}`, serviceInstanceId)
  }

  const roles = useMemo(() => {
    const allRoles = rolesQuery.data ?? []
    const normalizedSearch = search.trim().toLowerCase()
    const selectedGroup = selectedRoleGroupFilter === '__all__'
      ? null
      : groups.find((group) => readRoleGroupValue(group) === selectedRoleGroupFilter) ?? null

    const selectedGroupIds = selectedGroup
      ? new Set<string>([
          String(selectedGroup.id),
          readRoleGroupValue(selectedGroup),
        ])
      : null
    const selectedGroupTitle = String(selectedGroup?.title || '').trim().toLowerCase()

    const isRoleInSelectedGroup = (role: CatalogRole) => {
      if (!selectedGroupIds) {
        return true
      }

      const roleGroupId = readRoleGroupId(role)
      if (selectedGroupIds.has(roleGroupId)) {
        return true
      }

      if (!selectedGroupTitle) {
        return false
      }

      return String(role.role_group_name || '').trim().toLowerCase() === selectedGroupTitle
    }

    const matchesStatusFilter = (role: CatalogRole) => {
      if (statusFilter === 'active') {
        return isCatalogRoleActive(role)
      }
      if (statusFilter === 'inactive') {
        return !isCatalogRoleActive(role)
      }
      return true
    }

    return allRoles.filter((role) => {
      if (!isRoleInSelectedGroup(role) || !matchesStatusFilter(role)) {
        return false
      }
      if (!normalizedSearch) {
        return true
      }
      const roleName = String(role.name || '').toLowerCase()
      const roleCode = readRoleCode(role).toLowerCase()
      const roleGroup = String(role.role_group_name || '').toLowerCase()
      return roleName.includes(normalizedSearch) || roleCode.includes(normalizedSearch) || roleGroup.includes(normalizedSearch)
    })
  }, [groups, rolesQuery.data, search, selectedRoleGroupFilter, statusFilter])

  const filteredGroups = useMemo(() => {
    const normalizedSearch = groupSearch.trim().toLowerCase()
    if (!normalizedSearch) {
      return groups
    }

    return groups.filter((group) => String(group.title || '').toLowerCase().includes(normalizedSearch))
  }, [groupSearch, groups])

  useEffect(() => {
    if (selectedRoleGroupFilter === '__all__') {
      return
    }

    const selectedStillExists = groups.some((group) => readRoleGroupValue(group) === selectedRoleGroupFilter)
    if (!selectedStillExists) {
      setSelectedRoleGroupFilter('__all__')
    }
  }, [groups, selectedRoleGroupFilter])

  useEffect(() => {
    const currentParams = new URLSearchParams(searchParamsText)
    setSearch(currentParams.get('q') ?? '')
    setSelectedRoleGroupFilter(currentParams.get('group') ?? '__all__')
    setStatusFilter(readStatusFilterFromParams(currentParams.get('status')))
  }, [searchParamsText])

  useEffect(() => {
    const next = new URLSearchParams(searchParamsText)

    if (search.trim()) {
      next.set('q', search.trim())
    } else {
      next.delete('q')
    }

    if (selectedRoleGroupFilter !== '__all__') {
      next.set('group', selectedRoleGroupFilter)
    } else {
      next.delete('group')
    }

    if (statusFilter !== DEFAULT_STATUS_FILTER) {
      next.set('status', statusFilter)
    } else {
      next.delete('status')
    }

    const nextText = next.toString()
    if (searchParamsText !== nextText) {
      setSearchParams(next, { replace: true })
    }
  }, [search, searchParamsText, selectedRoleGroupFilter, setSearchParams, statusFilter])

  const resetFilters = () => {
    setSearch('')
    setSelectedRoleGroupFilter('__all__')
    setStatusFilter(DEFAULT_STATUS_FILTER)
  }

  const createMutation = useMutation({
    mutationFn: (payload: { role: string; role_group?: string | number; service_variant?: string | number; service_instance?: string | number }) => catalogRolesApi.create(payload),
    onSuccess: async () => {
      toast.success('Skill created')
      setFormOpen(false)
      setFormState(initialFormState)
      await queryClient.invalidateQueries({ queryKey: ['catalog-roles'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { role: string; role_group?: string | number; service_variant?: string | number; service_instance?: string | number } }) =>
      catalogRolesApi.update(id, payload),
    onSuccess: async () => {
      toast.success('Skill updated')
      setFormOpen(false)
      setFormState(initialFormState)
      await queryClient.invalidateQueries({ queryKey: ['catalog-roles'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => catalogRolesApi.deactivate(id),
    onSuccess: async () => {
      toast.success('Skill deactivated')
      setDeactivateTarget(null)
      await queryClient.invalidateQueries({ queryKey: ['catalog-roles'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const activateMutation = useMutation({
    mutationFn: ({ id, roleName }: { id: string; roleName: string }) =>
      catalogRolesApi.update(id, { role: roleName, active: 1 }),
    onSuccess: async () => {
      toast.success('Skill activated')
      await queryClient.invalidateQueries({ queryKey: ['catalog-roles'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const createGroupMutation = useMutation({
    mutationFn: (payload: { title: string; service_variant?: string | number; org_instance?: string | number }) => catalogRolesApi.createGroup(payload),
    onSuccess: async () => {
      toast.success('Skill group created')
      setGroupFormOpen(false)
      setGroupFormState(initialGroupFormState)
      await queryClient.invalidateQueries({ queryKey: ['catalog-role-groups'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const updateGroupMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { title: string; service_variant?: string | number; org_instance?: string | number } }) => catalogRolesApi.updateGroup(id, payload),
    onSuccess: async () => {
      toast.success('Skill group updated')
      setGroupFormOpen(false)
      setGroupFormState(initialGroupFormState)
      await queryClient.invalidateQueries({ queryKey: ['catalog-role-groups'] })
      await queryClient.invalidateQueries({ queryKey: ['catalog-roles'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const deleteGroupMutation = useMutation({
    mutationFn: (id: string) => catalogRolesApi.removeGroup(id),
    onSuccess: async () => {
      toast.success('Skill group hidden')
      setDeleteGroupTarget(null)
      await queryClient.invalidateQueries({ queryKey: ['catalog-role-groups'] })
      await queryClient.invalidateQueries({ queryKey: ['catalog-roles'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const openCreate = () => {
    setFormState(initialFormState)
    setFormOpen(true)
  }

  const openEdit = (role: CatalogRole) => {
    setFormState({
      id: String(role.id),
      role: String(role.name || ''),
      roleGroup: readRoleGroupId(role),
      maxAssignmentCount: role.max_assignment_count != null ? String(role.max_assignment_count) : '',
      primaryRole: Boolean(role.primary_role ?? role.default_role),
      appliesToOrganization: role.scope_type === 'organization',
      isSystemManaged: Boolean(role.is_system),
    })
    setFormOpen(true)
  }

  const openGroupCreate = () => {
    setGroupFormState(initialGroupFormState)
    setGroupFormOpen(true)
  }

  const openGroupEdit = (group: CatalogRoleGroup) => {
    setGroupFormState({
      id: String(group.id),
      title: String(group.title || ''),
    })
    setGroupFormOpen(true)
  }

  const submitForm = async () => {
    const roleName = formState.role.trim()
    if (!roleName) {
      toast.error('Role name is required')
      return
    }

    const maxAssignmentCount = formState.maxAssignmentCount.trim() ? Number(formState.maxAssignmentCount) : null
    if (
      formState.maxAssignmentCount.trim()
      && (!Number.isInteger(maxAssignmentCount) || (maxAssignmentCount ?? 0) < 1)
    ) {
      toast.error('Maximum assignment count must be a whole number greater than 0.')
      return
    }

    const payload: {
      role: string
      role_group?: string | number
      service_instance?: string | number
      apply_to_organization?: 0 | 1
      is_system?: 0 | 1
      primary_role?: 0 | 1
      max_assignment_count?: number
    } = {
      role: roleName,
      apply_to_organization: formState.appliesToOrganization ? 1 : 0,
      is_system: formState.isSystemManaged ? 1 : 0,
      primary_role: formState.primaryRole ? 1 : 0,
    }

    if (serviceInstanceId) {
      payload.service_instance = serviceInstanceId
    }

    if (formState.roleGroup) {
      payload.role_group = formState.roleGroup
    }

    if (Number.isInteger(maxAssignmentCount) && (maxAssignmentCount ?? 0) > 0) {
      payload.max_assignment_count = maxAssignmentCount!
    }

    if (formState.id) {
      await updateMutation.mutateAsync({ id: formState.id, payload })
      return
    }

    await createMutation.mutateAsync(payload)
  }

  const submitGroupForm = async () => {
    const title = groupFormState.title.trim()
    if (!title) {
      toast.error('Group title is required')
      return
    }

    const payload: { title: string; service_variant?: string | number; org_instance?: string | number } = {
      title,
    }

    if (serviceInstanceId) {
      payload.org_instance = serviceInstanceId
    }

    if (groupFormState.id) {
      await updateGroupMutation.mutateAsync({ id: groupFormState.id, payload })
      return
    }

    await createGroupMutation.mutateAsync(payload)
  }

  const isSaving = createMutation.isPending || updateMutation.isPending
  const isGroupSaving = createGroupMutation.isPending || updateGroupMutation.isPending
  const isLoading = rolesQuery.isLoading || groupsQuery.isLoading
  const errorMessage = rolesQuery.error ? getErrorMessage(rolesQuery.error) : null
  const availableFormGroups = formGroupsQuery.data ?? []
  const totalRoleCount = rolesQuery.data?.length ?? 0

  return (
    <div className="space-y-6 p-4 md:p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Skills
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div className="grid w-full gap-3 md:max-w-3xl md:grid-cols-3">
              <div className="space-y-1">
                <Label htmlFor="catalog-role-group-filter">Group</Label>
                <Select value={selectedRoleGroupFilter} onValueChange={setSelectedRoleGroupFilter}>
                  <SelectTrigger id="catalog-role-group-filter" className="h-9">
                    <SelectValue placeholder={groupsQuery.isLoading ? 'Loading groups...' : 'All groups'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All groups</SelectItem>
                    {groups.map((group) => (
                      <SelectItem key={String(group.id)} value={readRoleGroupValue(group)}>
                        {group.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="catalog-role-status-filter">Status</Label>
                <Select
                  value={statusFilter}
                  onValueChange={(value) => setStatusFilter(value as 'all' | 'active' | 'inactive')}
                >
                  <SelectTrigger id="catalog-role-status-filter" className="h-9">
                    <SelectValue placeholder="Active" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="all">All statuses</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="catalog-role-search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="catalog-role-search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className="h-9 pl-9"
                    placeholder="Search skills"
                  />
                </div>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button variant="outline" onClick={resetFilters}>
                Reset filters
              </Button>
              <Button onClick={openCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Add Skill
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : errorMessage ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              {errorMessage}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex justify-end text-sm text-muted-foreground">
                Showing {roles.length} of {totalRoleCount} skills
              </div>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead className="w-32 text-right">Worker Count</TableHead>
                      <TableHead>Group</TableHead>
                      <TableHead>Scope</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {roles.length > 0 ? roles.map((role) => (
                      <TableRow key={role.id}>
                        <TableCell className="font-medium">
                          <div className="space-y-1">
                            <Link to={buildSkillWorkersPath(role)} className="text-primary hover:underline">
                              {role.name}
                            </Link>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                              {(role.primary_role ?? role.default_role) ? (
                                <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">Primary</Badge>
                              ) : null}
                              {role.is_system ? (
                                <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">System</Badge>
                              ) : null}
                              {role.max_assignment_count ? (
                                <span>Max assignments: {role.max_assignment_count}</span>
                              ) : null}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-xs text-muted-foreground">
                            {readRoleCode(role) || '-'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">
                          <Link to={buildSkillWorkersPath(role)} className="text-primary hover:underline">
                            {readWorkerSkillCount(role)}
                          </Link>
                        </TableCell>
                        <TableCell>{role.role_group_name || '-'}</TableCell>
                        <TableCell>{getScopeLabel(role.scope_type)}</TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              isCatalogRoleActive(role)
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                : 'border-slate-200 bg-slate-50 text-slate-600'
                            }
                          >
                            {isCatalogRoleActive(role) ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                                <span className="sr-only">Actions</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              <DropdownMenuItem onClick={() => openEdit(role)}>
                                <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {!isCatalogRoleActive(role) ? (
                              <DropdownMenuItem
                                onClick={() => activateMutation.mutate({
                                  id: String(role.id),
                                  roleName: String(role.name || ''),
                                })}
                                disabled={activateMutation.isPending}
                              >
                                <CheckCircle2 className="mr-2 h-4 w-4" />
                                Activate
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setDeactivateTarget(role)}
                              >
                                <Ban className="mr-2 h-4 w-4" />
                                Deactivate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={7} className="h-20 text-center text-muted-foreground">
                        No skills found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {SHOW_CATALOG_ROLE_GROUPS_UI ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              Skill Groups
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex w-full flex-col gap-3 md:max-w-2xl md:flex-row md:items-end">
                <div className="relative w-full md:max-w-sm">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={groupSearch}
                    onChange={(event) => setGroupSearch(event.target.value)}
                    className="pl-9"
                    placeholder="Search skill groups"
                  />
                </div>
              </div>
              <Button onClick={openGroupCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Add Skill Group
              </Button>
            </div>

            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredGroups.length > 0 ? filteredGroups.map((group) => (
                      <TableRow key={String(group.id)}>
                        <TableCell className="font-medium">{group.title}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                                <span className="sr-only">Actions</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              <DropdownMenuItem onClick={() => openGroupEdit(group)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setDeleteGroupTarget(group)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Hide
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={2} className="h-20 text-center text-muted-foreground">
                          No skill groups found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{formState.id ? 'Edit Skill' : 'Add Skill'}</DialogTitle>
            <DialogDescription>
              Define skill name, group, limits, and scope for skill management.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Skill Group</Label>
              <Select
                value={formState.roleGroup || '__none__'}
                onValueChange={(value) => setFormState((prev) => ({
                  ...prev,
                  roleGroup: value === '__none__' ? '' : value,
                }))}
                disabled={formGroupsQuery.isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    formGroupsQuery.isLoading
                      ? 'Loading groups...'
                      : 'Select a skill group'
                  }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No group</SelectItem>
                  {availableFormGroups.map((group: CatalogRoleGroup) => (
                    <SelectItem key={String(group.id)} value={readRoleGroupValue(group)}>
                      {group.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="catalog-role-name">Skill Name</Label>
              <Input
                id="catalog-role-name"
                value={formState.role}
                onChange={(event) => setFormState((prev) => ({ ...prev, role: event.target.value }))}
                placeholder="Enter skill name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="catalog-role-max-assignments">Maximum Assignments</Label>
              <Input
                id="catalog-role-max-assignments"
                type="number"
                min={1}
                step={1}
                value={formState.maxAssignmentCount}
                onChange={(event) => setFormState((prev) => ({ ...prev, maxAssignmentCount: event.target.value }))}
                placeholder="Leave blank for no limit"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <div className="flex flex-wrap items-center gap-6 rounded-md border border-border/60 bg-muted/20 px-3 py-2">
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={formState.primaryRole}
                    onChange={(event) => setFormState((prev) => ({ ...prev, primaryRole: event.target.checked }))}
                  />
                  Primary skill
                </label>
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={formState.appliesToOrganization}
                    onChange={(event) => setFormState((prev) => ({ ...prev, appliesToOrganization: event.target.checked }))}
                  />
                  Apply to organization
                </label>
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={formState.isSystemManaged}
                    onChange={(event) => setFormState((prev) => ({
                      ...prev,
                      isSystemManaged: event.target.checked,
                      appliesToOrganization: event.target.checked ? false : prev.appliesToOrganization,
                    }))}
                  />
                  System skill
                </label>
              </div>
              {formState.isSystemManaged ? (
                <p className="text-xs text-muted-foreground">
                  System skills are shared platform-wide and must use a system-managed skill group.
                </p>
              ) : null}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={submitForm} disabled={isSaving}>
              {isSaving ? 'Saving...' : formState.id ? 'Update Skill' : 'Create Skill'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deactivateTarget)} onOpenChange={(open) => { if (!open) setDeactivateTarget(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate Skill</DialogTitle>
            <DialogDescription>
              This will deactivate skill {deactivateTarget?.name ? `"${deactivateTarget.name}"` : ''}.
              Skills cannot be deleted because they may be linked to worker skills and assignments.
              You can activate the skill again later from the actions menu.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateTarget(null)} disabled={deactivateMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deactivateMutation.isPending || !deactivateTarget}
              onClick={async () => {
                if (!deactivateTarget) {
                  return
                }
                await deactivateMutation.mutateAsync(String(deactivateTarget.id))
              }}
            >
              {deactivateMutation.isPending ? 'Deactivating...' : 'Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {SHOW_CATALOG_ROLE_GROUPS_UI ? (
        <Dialog open={groupFormOpen} onOpenChange={setGroupFormOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{groupFormState.id ? 'Edit Skill Group' : 'Add Skill Group'}</DialogTitle>
              <DialogDescription>
                Configure skill group title used by skills.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <Label htmlFor="catalog-role-group-title">Group Title</Label>
              <Input
                id="catalog-role-group-title"
                value={groupFormState.title}
                onChange={(event) => setGroupFormState((prev) => ({ ...prev, title: event.target.value }))}
                placeholder="Enter group title"
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setGroupFormOpen(false)} disabled={isGroupSaving}>
                Cancel
              </Button>
              <Button onClick={submitGroupForm} disabled={isGroupSaving}>
                {isGroupSaving ? 'Saving...' : groupFormState.id ? 'Update Group' : 'Create Group'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}

      {SHOW_CATALOG_ROLE_GROUPS_UI ? (
        <Dialog open={Boolean(deleteGroupTarget)} onOpenChange={(open) => { if (!open) setDeleteGroupTarget(null) }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Hide Skill Group</DialogTitle>
              <DialogDescription>
                This will hide group {deleteGroupTarget?.title ? `"${deleteGroupTarget.title}"` : ''} from active lists.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteGroupTarget(null)} disabled={deleteGroupMutation.isPending}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={deleteGroupMutation.isPending || !deleteGroupTarget}
                onClick={async () => {
                  if (!deleteGroupTarget) {
                    return
                  }
                  await deleteGroupMutation.mutateAsync(String(deleteGroupTarget.id))
                }}
              >
                {deleteGroupMutation.isPending ? 'Hiding...' : 'Hide'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  )
}
